import { useMemo, useState } from 'react';
import {
  createMasterItem,
  deleteMasterItem,
  updateMasterItem,
} from '../api/dashboardApi.js';
import '../styles/dashboard-master.css';

function buildEmptyRow(definition, supportingData) {
  return definition.fields.reduce(
    (acc, field) => ({
      ...acc,
      [field.key]: field.getDefaultValue
        ? field.getDefaultValue({ supportingData })
        : (field.type === 'checkbox' ? false : ''),
    }),
    {},
  );
}

export default function MasterCrudTable({
  definition,
  items,
  supportingData,
  onReload,
  readOnly = false,
}) {
  const rows = useMemo(
    () => items.map((item) => definition.fromApi(item)),
    [definition, items],
  );

  const [modalValues, setModalValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const oemByName = useMemo(() => new Map(
    (supportingData.oems || []).map((item) => [
      String(item.oem || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.oems]);

  const regionByName = useMemo(() => new Map(
    (supportingData.regions || []).map((item) => [
      String(item.region || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.regions]);

  const largeCategoryByName = useMemo(() => new Map(
    (supportingData.largeCategories || []).map((item) => [
      String(item.large_category || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.largeCategories]);

  const middleCategoryByName = useMemo(() => new Map(
    (supportingData.middleCategories || []).map((item) => [
      String(item.middle_category || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.middleCategories]);

  const categoryByName = useMemo(() => new Map(
    (supportingData.categories || []).map((item) => [
      String(item.category || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.categories]);

  const smallCategoryByName = useMemo(() => new Map(
    (supportingData.smallCategories || []).map((item) => [
      String(item.small_category || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.smallCategories]);

  const actorByName = useMemo(() => new Map(
    (supportingData.actors || []).map((item) => [
      String(item.actor || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.actors]);

  const departmentByName = useMemo(() => new Map(
    (supportingData.departments || []).map((item) => [
      String(item.department || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.departments]);

  const functionByName = useMemo(() => new Map(
    (supportingData.functions || []).map((item) => [
      String(item.function || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.functions]);

  const permissionByName = useMemo(() => new Map(
    (supportingData.permissions || []).map((item) => [
      String(item.permission || '').trim().toLocaleLowerCase('ja'),
      item.id,
    ]),
  ), [supportingData.permissions]);

  const definitionContext = useMemo(() => ({
    oemByName,
    regionByName,
    largeCategoryByName,
    middleCategoryByName,
    categoryByName,
    smallCategoryByName,
    actorByName,
    departmentByName,
    functionByName,
    permissionByName,
    supportingData,
  }), [oemByName, regionByName, largeCategoryByName, middleCategoryByName, categoryByName, smallCategoryByName, actorByName, departmentByName, functionByName, permissionByName, supportingData]);

  const handleOpen = (row) => {
    setModalValues({ ...row });
    setModalError('');
  };

  const handleAdd = () => {
    setModalValues(buildEmptyRow(definition, supportingData));
    setModalError('');
  };

  const handleClose = () => {
    setModalValues(null);
    setModalError('');
  };

  const handleSave = async () => {
    const errors = definition.validateRow(modalValues, definitionContext) || [];
    if (errors.length > 0) {
      setModalError(errors.join('\n'));
      return;
    }

    setSaving(true);
    setModalError('');
    try {
      const payload = definition.toApiPayload(modalValues, definitionContext);
      if (modalValues.id) {
        await updateMasterItem(definition.endpoint, modalValues.id, payload);
      } else {
        await createMasterItem(definition.endpoint, payload);
      }
      handleClose();
      await onReload();
    } catch (err) {
      setModalError(err.message || '保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    /* istanbul ignore if -- 削除ボタンは編集モーダル（id あり）にしか出ない */
    if (!modalValues?.id) return;
    const label = definition.fields.map((f) => modalValues[f.key]).filter(Boolean).join(' / ');
    if (!window.confirm(`「${label}」を削除しますか？`)) return;

    setSaving(true);
    setModalError('');
    try {
      await deleteMasterItem(definition.endpoint, modalValues.id);
      handleClose();
      await onReload();
    } catch (err) {
      setModalError(err.message || '削除に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setModalValues((prev) => ({ ...prev, [key]: value }));
  };

  const renderModalField = (field) => {
    /* istanbul ignore if -- displayOnly はモーダル描画前に filter で除かれている */
    if (field.displayOnly) return null;

    const isReadOnly = field.readOnlyOnEdit && modalValues.id;

    if (isReadOnly) {
      return (
        <span className="ml-modal-readonly">{modalValues[field.key] || ''}</span>
      );
    }

    if (field.type === 'select') {
      const options = field.getOptions({ row: modalValues, rows, supportingData });
      return (
        <select
          className="ml-modal-input"
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          value={modalValues[field.key] || ''}
        >
          {options.map((opt) => (
            <option key={opt || '__empty'} value={opt}>
              {field.getOptionLabel
                ? field.getOptionLabel({ value: opt, supportingData })
                : (opt || '選択してください')}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label className="ml-modal-checkbox">
          <input
            checked={Boolean(modalValues[field.key])}
            onChange={(e) => handleFieldChange(field.key, e.target.checked)}
            type="checkbox"
          />
          {field.label}
        </label>
      );
    }

    if (field.type === 'date') {
      return (
        <input
          className="ml-modal-input"
          max="9999-12-31"
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          placeholder={field.label}
          type="date"
          value={modalValues[field.key] || ''}
        />
      );
    }

    return (
      <input
        className="ml-modal-input"
        onChange={(e) => handleFieldChange(field.key, e.target.value)}
        placeholder={field.label}
        type="text"
        value={modalValues[field.key] || ''}
      />
    );
  };

  return (
    <div className="mc-container">
      <div className="mc-header">
        <h3 className="mc-title">{definition.label}</h3>
        {!readOnly && (
          <button className="mc-add-button" onClick={handleAdd} type="button">
            + 追加
          </button>
        )}
      </div>

      <div className="mc-table-wrap">
        <table className="mc-table">
          <thead>
            <tr>
              {definition.fields.map((field) => (
                <th key={field.key}>{field.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} onClick={readOnly ? undefined : () => handleOpen(row)} style={readOnly ? { cursor: 'default' } : undefined}>
                {definition.fields.map((field) => (
                  <td key={field.key}>
                    {field.type === 'checkbox'
                      ? (row[field.key] ? 'はい' : 'いいえ')
                      : (row[field.key] || '')}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="mc-empty" colSpan={definition.fields.length}>
                  データがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modalValues !== null && (
        <div className="ml-overlay" onClick={handleClose}>
          <div className="ml-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ml-modal-title">
              {modalValues.id ? '編集' : '新規追加'}
            </h3>
            <div className="ml-modal-body">
              {definition.fields.filter((f) => !f.displayOnly).map((field) => (
                field.type === 'checkbox' ? (
                  <div className="ml-modal-field" key={field.key}>
                    {renderModalField(field)}
                  </div>
                ) : (
                  <div className="ml-modal-field" key={field.key}>
                    <label className="ml-modal-label">
                      {field.label}{field.required && ' *'}
                    </label>
                    {renderModalField(field)}
                  </div>
                )
              ))}
            </div>
            {modalError && (
              <div className="ml-modal-error">{modalError}</div>
            )}
            <div className="ml-modal-footer">
              {modalValues.id && (
                <button className="ml-delete-button" disabled={saving} onClick={handleDelete} type="button">
                  削除
                </button>
              )}
              <div className="ml-modal-footer-right">
                <button className="ml-cancel-button" onClick={handleClose} type="button">
                  キャンセル
                </button>
                <button className="ml-save-button" disabled={saving} onClick={handleSave} type="button">
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
